import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import headerStyle from './Header.module.scss';
import { RoutesUrl } from '../../router/RouterUrls';
import { Languages } from '../../emuns/enums';
import { profile } from '../../data/profile';
import { useScrolled } from '../../hooks/useScrolled';
import IButton from '../elements/iButton/IButton';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const navItems = [
    { route: RoutesUrl.home, key: 'header.home' },
    { route: RoutesUrl.about, key: 'header.about' },
    { route: RoutesUrl.projects, key: 'header.projects' },
    { route: RoutesUrl.contacts, key: 'header.contacts' },
];

const Header = observer(() => {
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const isScrolled = useScrolled(8);

    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    // Close the mobile menu whenever the route changes.
    useEffect(() => {
        setIsMenuOpen(false);
    }, [pathname]);

    // Lock body scroll while the mobile menu is open.
    useEffect(() => {
        document.body.style.overflow = isMenuOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [isMenuOpen]);

    const navigationHandler = (route: string) => {
        navigate(route);
        setIsMenuOpen(false);
    };

    const toggleLanguageHandler = async () => {
        const next = i18n.language === Languages.HEBREW ? Languages.ENGLISH : Languages.HEBREW;
        try {
            await i18n.changeLanguage(next);
        } catch (error) {
            console.error(error);
        }
    };

    const downloadResumeHandler = () => {
        const link = document.createElement('a');
        link.href = window.location.origin + profile.resumePath;
        link.download = profile.resumeFilename;
        link.click();
    };

    return (
        <header className={`${headerStyle.header} ${isScrolled ? headerStyle.scrolled : ''}`}>
            <div className={headerStyle.inner}>
                <button type='button' className={headerStyle.brand} onClick={() => navigationHandler(RoutesUrl.home)}>
                    <span className={headerStyle.monogram}>{profile.initials}</span>
                    <span className={headerStyle.brandText}>
                        <span className={headerStyle.brandName}>{profile.name}</span>
                        <span className={headerStyle.brandRole}>
                            {profile.role} @ {profile.company}
                        </span>
                    </span>
                </button>

                <nav className={headerStyle.nav} aria-label='Primary'>
                    {navItems.map((item) => (
                        <button
                            key={item.route}
                            type='button'
                            className={`${headerStyle.navLink} ${pathname === item.route ? headerStyle.active : ''}`}
                            onClick={() => navigationHandler(item.route)}
                            aria-current={pathname === item.route ? 'page' : undefined}
                        >
                            {t(item.key)}
                        </button>
                    ))}
                </nav>

                <div className={headerStyle.actions}>
                    <button
                        type='button'
                        className={headerStyle.langToggle}
                        onClick={toggleLanguageHandler}
                        aria-label={t('header.switchLanguage')}
                    >
                        {i18n.language === Languages.HEBREW ? 'EN' : 'עב'}
                    </button>

                    <span className={headerStyle.resumeButton}>
                        <IButton
                            title={t('header.resume')}
                            onClick={downloadResumeHandler}
                            variant='ghost'
                            icon={<ArrowDownwardIcon />}
                        />
                    </span>

                    <button
                        type='button'
                        className={headerStyle.menuToggle}
                        onClick={() => setIsMenuOpen((open) => !open)}
                        aria-label={isMenuOpen ? t('header.closeMenu') : t('header.openMenu')}
                        aria-expanded={isMenuOpen}
                    >
                        {isMenuOpen ? <CloseIcon /> : <MenuIcon />}
                    </button>
                </div>
            </div>

            {isMenuOpen && (
                <div className={headerStyle.mobileMenu}>
                    <nav className={headerStyle.mobileNav} aria-label='Mobile'>
                        {navItems.map((item) => (
                            <button
                                key={item.route}
                                type='button'
                                className={`${headerStyle.mobileLink} ${pathname === item.route ? headerStyle.active : ''}`}
                                onClick={() => navigationHandler(item.route)}
                            >
                                {t(item.key)}
                            </button>
                        ))}
                    </nav>
                    <div className={headerStyle.mobileFooter}>
                        <IButton
                            title={t('header.resume')}
                            onClick={downloadResumeHandler}
                            variant='primary'
                            fullWidth
                            icon={<ArrowDownwardIcon />}
                        />
                    </div>
                </div>
            )}
        </header>
    );
});

export default Header;
